import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { FlatList } from 'react-native';
import { formatCurrency } from '@/utils/money';
import { formatDateWithYear } from '@/utils/date';
import { ListItemSeparator } from '@/components/list-item';
import { Payments_GetCollectedManualPaymentsQuery } from '@/types';
import { PaymentsListItem } from './payments-list-item';
import { Typography } from '@usepepper/ui/atoms/typography';

type CollectedPayment =
  Payments_GetCollectedManualPaymentsQuery['getCollectedManualPayments']['payments'][number];

/**
 * "2 invoices + 1 credit memo" — a payment can apply credit memos as well as
 * invoices, and reporting only the invoices understates what it covers.
 */
const formatCoverage = ({
  creditMemosCount,
  invoicesCount,
}: {
  creditMemosCount: number;
  invoicesCount: number;
}) => {
  const parts: string[] = [];

  if (invoicesCount > 0) {
    parts.push(
      invoicesCount === 1
        ? i18n.t('bank_deposits.invoice_count.invoice', {
            count: invoicesCount,
          })
        : i18n.t('bank_deposits.invoice_count.invoices', {
            count: invoicesCount,
          }),
    );
  }

  if (creditMemosCount > 0) {
    parts.push(
      creditMemosCount === 1
        ? i18n.t('bank_deposits.credit_memo_count.credit_memo', {
            count: creditMemosCount,
          })
        : i18n.t('bank_deposits.credit_memo_count.credit_memos', {
            count: creditMemosCount,
          }),
    );
  }

  return parts.join(' + ');
};

interface Props {
  currencyCode: string | null;
  payments: CollectedPayment[];
}

export const CollectedPaymentsList: React.FC<Props> = ({
  currencyCode,
  payments,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();

  return (
    <FlatList
      data={payments}
      ItemSeparatorComponent={() => (
        <ListItemSeparator style={styles.separator} />
      )}
      keyExtractor={(payment) => payment.payment_batch_id}
      renderItem={({ item: payment }) => (
        <PaymentsListItem
          bottomLeft={
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm"
            >
              {payment.payment_batch?.restaurant?.name} ·{' '}
              {formatCoverage({
                creditMemosCount: payment.credit_memos_count,
                invoicesCount: payment.invoices_count,
              })}
            </Typography>
          }
          bottomRight={
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm"
            >
              {formatDateWithYear(payment.collected_date)}
            </Typography>
          }
          topLeft={
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base-600"
            >
              {payment.payment_method_display_text ?? i18n.t('cash')}
            </Typography>
          }
          topRight={
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {formatCurrency(payment.total_amount_in_micros, currencyCode)}
            </Typography>
          }
        />
      )}
    />
  );
};

const useStyles = createStyles((theme) => ({
  separator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: theme.sk.spacing.spaceMedium,
  },
}));
